import React, { useCallback, useContext, useMemo, useRef } from "react";
import { Alert, Box, Card, Divider, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import {
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_PAGE_SIZE_OPTIONS,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_SX,
} from "../../../components/ui/dataGridStyles";
import { StateContext } from "../../../components/context/StateProvider";
import Toasts from "../../../components/notificacion/Toasts";
import Utils from "../../../utils/Utils";
import successSound from "../../../assets/audio/success.mp3";
import errorSound from "../../../assets/audio/error.mp3";
import { EstadosVentasMobileList } from "../components/lists";
import { INTERVALO_REFRESCO_VENTAS_MS } from "../constants";
import { generateEstadosVentasColumns } from "../helpers/columnsEstadosVentas";
import {
    CLASE_FILA_MOSTRADOR,
    COLOR_FILA_MOSTRADOR,
    getEtiquetaEstadoVenta,
    prepararFilasEstadosVentas,
} from "../helpers/estadosVentas";
import usePedidosVenta from "../hooks/usePedidosVenta";
import { escaparHtml } from "../utils/formatters";

const MENSAJE_ERROR = "No se pudieron cargar los estados de ventas";
const MENSAJE_ERROR_ACCION = "No se pudo cambiar el estado";
const SX_CARD = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 };
const SX_TITULO = { fontWeight: 700, color: "#212529", mb: 0.5, display: "flex", alignItems: "center", gap: 1 };
const SX_SUBTITULO = { color: "#6c757d" };
const ESTILO_MOSTRADOR = { backgroundColor: COLOR_FILA_MOSTRADOR.fondo, color: COLOR_FILA_MOSTRADOR.texto };
// El hover propio va después del genérico de DATA_GRID_SX y con más especificidad (sustituye al !important del legacy)
const SX_GRID = {
    ...DATA_GRID_SX,
    [`& .MuiDataGrid-row.${CLASE_FILA_MOSTRADOR}`]: ESTILO_MOSTRADOR,
    [`& .MuiDataGrid-row.${CLASE_FILA_MOSTRADOR}:hover`]: ESTILO_MOSTRADOR,
};
const getRowId = (fila) => fila.id;
const getRowClassName = (params) => (params.row.esMostrador ? CLASE_FILA_MOSTRADOR : "");
// Se revisa la jornada completa de un vistazo (como EstadosCompras): 100 filas por página
const INITIAL_STATE = { pagination: { paginationModel: { pageSize: 100 } } };

const reproducir = (sonido) => {
    try { Utils.playAudio(sonido); } catch (e) { /* el audio es accesorio: no rompe el flujo */ }
};

/**
 * Página "Control pedidos venta": ventas pendientes del ejercicio con refresco silencioso,
 * cambio de estado por fila (toast y sonido) y vista móvil en tarjetas
 */
const EstadosVentas = () => {
    const { state, updateState, getNombreAlmacen } = useContext(StateContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const { pedidos, loading, error, cambiandoId, cambiarEstado } = usePedidosVenta(state.year, {
        intervaloMs: INTERVALO_REFRESCO_VENTAS_MS,
    });
    const filas = useMemo(
        () => prepararFilasEstadosVentas(pedidos, getNombreAlmacen),
        // state.almacenes: getNombreAlmacen lee de ahí y el nombre de nave debe actualizarse al cargarlos
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pedidos, state.almacenes, getNombreAlmacen]
    );
    // Refs para que el handler sea estable aunque cambien los toasts (columnas memoizadas)
    const toastsRef = useRef(state.toasts);
    toastsRef.current = state.toasts;
    const updateStateRef = useRef(updateState);
    updateStateRef.current = updateState;

    const avisar = useCallback((mensaje, tipo, sonido) => {
        const toast = Toasts.generateToast(mensaje, tipo);
        updateStateRef.current({ toasts: [...toastsRef.current, toast] });
        reproducir(sonido);
    }, []);

    const onCambiarEstado = useCallback(async (fila, estado) => {
        try {
            const respuesta = await cambiarEstado(fila, estado);
            if (respuesta === undefined) return; // doble clic ignorado: no es un éxito
            const etiqueta = escaparHtml(getEtiquetaEstadoVenta(estado).toLowerCase());
            avisar(`${escaparHtml(fila.numero)} ha sido cambiado al estado: <strong>${etiqueta}</strong>`, "success", successSound);
        } catch (e) {
            avisar(escaparHtml(e?.message || MENSAJE_ERROR_ACCION), "error", errorSound);
        }
    }, [cambiarEstado, avisar]);

    const columnas = useMemo(
        () => generateEstadosVentasColumns({ onCambiarEstado, cambiandoId }),
        [onCambiarEstado, cambiandoId]
    );

    return (
        <Box sx={{ width: "100%" }}>
            <Card sx={SX_CARD}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography variant={isMobile ? "h6" : "h5"} sx={SX_TITULO}>
                        <FactCheckIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                        Control pedidos venta
                    </Typography>
                    <Typography variant="body2" sx={SX_SUBTITULO}>
                        Pedidos de venta pendientes del ejercicio {state.year}
                    </Typography>
                    {error !== null && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Alert severity="error">{error || MENSAJE_ERROR}</Alert>
                        </>
                    )}
                </Box>
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {isMobile ? (
                        <EstadosVentasMobileList
                            rows={filas}
                            loading={loading}
                            cambiandoId={cambiandoId}
                            onCambiarEstado={onCambiarEstado}
                        />
                    ) : (
                        <DataGrid
                            rows={filas}
                            columns={columnas}
                            getRowId={getRowId}
                            getRowClassName={getRowClassName}
                            localeText={DATA_GRID_LOCALE_TEXT}
                            autoHeight
                            loading={loading}
                            disableRowSelectionOnClick
                            initialState={INITIAL_STATE}
                            pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={SX_GRID}
                        />
                    )}
                </Box>
            </Card>
        </Box>
    );
};

export default EstadosVentas;
